// EAZYFLEET inventory SKU taxonomy & helpers.
// Standard 12-char SKU: MAIN-SUB-BRAND-PART  (XX-XX-XXXX-XXXX)
// Rules: alphanumeric only, no "O" / "I" (confusable with 0/1 on bin labels).

export const TAXONOMY = [
    {
        code: "EN", name: "Engine & Performance", subs: [
            { code: "EN-FL", name: "Service Filters (Oil, Air, Fuel)" },
            { code: "EN-VT", name: "Valvetrain & Internals (Valves, Cams, Pistons)" },
            { code: "EN-FD", name: "Fuel Delivery (Injectors, Pumps, Rails)" },
            { code: "EN-GS", name: "Engine Sealing & Gaskets" },
            { code: "EN-CL", name: "Thermal Management & Cooling" },
        ]
    },
    {
        code: "DT", name: "Drivetrain & Transmission", subs: [
            { code: "DT-CW", name: "Clutch & Flywheel Systems" },
            { code: "DT-AX", name: "Axles & Driveshafts" },
            { code: "DT-TM", name: "Transmission Maintenance (Filters, Pans)" },
            { code: "DT-DF", name: "Differential & Final Drive" },
        ]
    },
    {
        code: "CH", name: "Chassis, Steering & Braking", subs: [
            { code: "CH-BR", name: "Braking Systems (Pads, Rotors, Calipers)" },
            { code: "CH-SS", name: "Suspension (Shocks, Struts, Springs)" },
            { code: "CH-ST", name: "Steering Linkages (Tie Rods, Racks)" },
        ]
    },
    {
        code: "EL", name: "Electrical & Electronics", subs: [
            { code: "EL-IG", name: "Ignition System (Plugs, Coils)" },
            { code: "EL-SC", name: "Starting & Charging (Batteries, Alternators)" },
            { code: "EL-SN", name: "Engine Electronics & Sensors" },
            { code: "EL-LT", name: "Lighting & Cabin Comfort" },
        ]
    },
    {
        code: "EX", name: "Exhaust & Emissions", subs: [
            { code: "EX-PP", name: "Exhaust Piping & Sound Control" },
            { code: "EX-EM", name: "Smog & Emissions" },
        ]
    },
    {
        code: "FL", name: "Fluids & Consumables", subs: [
            { code: "FL-LB", name: "Lubricants (Engine & Gear Oils)" },
            { code: "FL-HY", name: "Hydraulic Fluids (Brake, ATF)" },
            { code: "FL-CH", name: "Workshop Chemicals & Sealants" },
        ]
    },
];

// Suggested 4-char brand codes (alphanumeric, no O / I).
export const BRAND_SUGGESTIONS = [
    { code: "BOSC", name: "Bosch" },
    { code: "NGKP", name: "NGK Plugs" },
    { code: "KNFI", name: "K&N Filters" },
    { code: "FELP", name: "Fel-Pro" },
    { code: "BILS", name: "Bilstein" },
    { code: "MONR", name: "Monroe" },
    { code: "DELP", name: "Delphi" },
    { code: "MOB1", name: "Mobil 1" },
];

// 12-char SKU regex: 2-2-4-4, alphanumeric, no O or I.
// Pattern: ^[A-HJ-NP-Z0-9]{2}-[A-HJ-NP-Z0-9]{2}-[A-HJ-NP-Z0-9]{4}-[A-HJ-NP-Z0-9]{4}$
const SKU_CHAR_CLASS = "[A-HJ-NP-Z0-9]";
const SKU_REGEX = new RegExp(
    `^${SKU_CHAR_CLASS}{2}-${SKU_CHAR_CLASS}{2}-${SKU_CHAR_CLASS}{4}-${SKU_CHAR_CLASS}{4}$`
);

export const SKU_HELP = (
    `Standard 12-character SKU: [MAIN]-[SUB]-[BRAND]-[PART]\n` +
    `   • 2 + 2 + 4 + 4 alphanumeric characters, dashes between groups\n` +
    `   • Allowed: A–Z (NO 'O' / 'I'), 0–9\n` +
    `   • Example: EN-FL-KNFI-1001 (K&N Oil Filter HP-1001)`
);

export const SKU_FORMAT_NOTE = "XX-XX-XXXX-XXXX · A–Z (no O/I) + 0–9";

/** Strip any disallowed chars and uppercase, keep only A-HJ-NP-Z0-9. */
export function sanitizeSkuToken(value, len) {
    const cleaned = (value || "")
        .toUpperCase()
        .replace(/[^A-HJ-NP-Z0-9]/g, "");
    return len ? cleaned.slice(0, len) : cleaned;
}

/** Build SKU from segments. Missing segments are returned blank with placeholders shown only in UI. */
export function buildSku({ main, sub, brand, part }) {
    const m = sanitizeSkuToken(main, 2);
    // sub is stored full (EN-FL) — take only its 2-letter suffix
    const subRaw = (sub || "").split("-").pop();
    const s = sanitizeSkuToken(subRaw, 2);
    const b = sanitizeSkuToken(brand, 4);
    const p = sanitizeSkuToken(part, 4);
    return `${m}-${s}-${b}-${p}`;
}

export function isValidSku(sku) {
    return SKU_REGEX.test((sku || "").toUpperCase());
}

/** Quick lookup helpers */
export function getMain(code) {
    return TAXONOMY.find((m) => m.code === code) || null;
}

export function getSubsForMain(code) {
    return getMain(code)?.subs || [];
}
